import type { ApiResponse } from '@macroinsight/shared-types';

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(path);
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers: defaultHeaders,
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401 && !isRetry && typeof window !== 'undefined') {
          const refreshUrl = this.buildUrl('api/auth/refresh');
          const refreshRes = await fetch(refreshUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          });
          if (refreshRes.ok) {
            return this.request<T>(path, options, true);
          }
        }
        if (response.status === 401) {
          return {
            success: false,
            data: null,
            message: 'Unauthorized'
          };
        }

        const errorData = await response.json().catch(() => ({
          message: `Request failed with status ${response.status}`
        }));

        return {
          success: false,
          data: null,
          message: errorData.message || `Request failed with status ${response.status}`
        };
      }

      const data = await response.json();

      // Handle backend response format: { success: true, data: {...}, message: "..." }
      if (data && typeof data === 'object' && 'success' in data) {
        if (data.success === false) {
          return {
            success: false,
            data: null,
            message: data.message || 'Request failed'
          };
        }

        return {
          success: true,
          data: data.data as T,
          message: data.message || 'Request successful'
        };
      }

      // Fallback: assume the entire response is the data
      return {
        success: true,
        data: data as T,
        message: 'Request successful'
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Network error occurred'
      };
    }
  }

  async get<T>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  async post<T>(path: string, body: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(path, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async put<T>(path: string, body: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(path, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body)
    });
  }

  async patch<T>(path: string, body: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(path, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  }

  async delete<T>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  private buildUrl(path: string): string {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }

    const base = this.baseUrl.replace(/\/+$/, '');
    const cleanPath = path.replace(/^\/+/, '');

    return `${base}/${cleanPath}`;
  }
}
